import { sql } from "drizzle-orm";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { flags } from "@/config";
import { db } from "@/db/db";
import { executeQuery, Tx } from "@/db/drizzleUtils";
import { accountEvent, feed, item, permission, workspaceMembership } from "@/db/schema";
import type { WorkspaceMembership } from "@/db/types";
import { emitTableUpdates } from "../eventing/TableEventManager";
import { unreadClearEvents } from "../UnreadsContextProvider";

const fetchHideDriverMessageValue = (organizer: boolean) => {
  const hideDriverMessagesString = localStorage?.getItem("hideDriverMessages") ?? false;
  return hideDriverMessagesString === "true" && organizer;
};

function getFeedLatestEventCTE(
  membership: WorkspaceMembership,
  feedIds?: string[],
  options?: { isAliasFeed?: boolean; teamDispatchMode?: boolean },
) {
  const clearEventsSql = sql.join(unreadClearEvents, sql`, `);
  const feedJoin =
    options?.isAliasFeed !== undefined
      ? sql`join ${feed} on ${permission.feedId} = ${feed.id} and ${feed.isAliasFeed} = ${options.isAliasFeed}`
      : sql``;

  const workspaceMembershipJoin =
    options?.teamDispatchMode === true
      ? sql`
      join ${workspaceMembership} 
      on ${workspaceMembership.workspaceId} = ${membership.workspaceId}
      and ${workspaceMembership.role} = 'member'`
      : sql``;

  const accountIdCondition =
    options?.teamDispatchMode === true
      ? sql`${accountEvent.accountId} = ${workspaceMembership.accountId}`
      : sql`${accountEvent.accountId} = ${membership.accountId}`;

  return sql`
    select 
    ${permission.feedId},
    MAX(${accountEvent.createdAt}) as "latestEvent",
    MAX(${permission.updatedAt}) as "permissionUpdated"

    from ${permission}

    ${feedJoin}
    ${workspaceMembershipJoin}

    left outer join ${accountEvent} 
    on ${accountEvent.feedId} = ${permission.feedId}
    and ${accountIdCondition}
    and ${accountEvent.name} in (${clearEventsSql})
    
    where ${permission.workspaceMembershipId} = ${membership.id}
    and ${permission.name} = 'read'
    and ${permission.enabled} = true
    ${feedIds ? sql`and ${permission.feedId} in (${sql.join(feedIds, sql`, `)})` : sql``}

    group by ${permission.feedId}
  `;
}

// Helper function to generate timestamp comparison
function getTimestampComparison(operator: ">" | "<=") {
  return sql`
    ${item.createdAt} ${sql.raw(operator)} 
    GREATEST(
      COALESCE(feed_latest_event."latestEvent", '-infinity'::timestamp), 
      COALESCE(feed_latest_event."permissionUpdated", '-infinity'::timestamp)
    )
  `;
}

// Helper function for common conditions
function getCommonItemConditions(
  membership: WorkspaceMembership,
  options?: { isFromAliasMember?: boolean; unreadStatus?: boolean },
) {
  return sql`
    ${item.feedId} = feed_latest_event."feedId"
    and ${item.accountId} != ${membership.accountId}
    ${options?.isFromAliasMember !== undefined ? sql`and ${item.isFromAliasMember} = ${options.isFromAliasMember}` : sql``}
    ${options?.unreadStatus !== undefined ? sql`and ${item.unread} = ${options.unreadStatus}` : sql``}
    and ${item.unreadNonMutable} = false
    and ${item.isFromBot} = false
  `;
}

function getMyUnreadAllQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds);

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = true
  from feed_latest_event
  where ${getCommonItemConditions(membership)}
  and ${getTimestampComparison(">")}
  `;
}

function getMyReadAllQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds);

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = false
  from feed_latest_event
  where ${getCommonItemConditions(membership, { unreadStatus: true })}
  and ${getTimestampComparison("<=")}
  `;
}

function getMyUnreadsNonAliasQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds, { isAliasFeed: false });

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = true
  from feed_latest_event
  where ${getCommonItemConditions(membership)}
  and ${getTimestampComparison(">")}
  `;
}

function getMyReadsNonAliasQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds, { isAliasFeed: false });

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = false 
  from feed_latest_event
  where ${getCommonItemConditions(membership, { unreadStatus: true })}
  and ${getTimestampComparison("<=")}
  `;
}

function getMyUnreadsAliasQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds, { isAliasFeed: true });

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = true
  from feed_latest_event
  where ${getCommonItemConditions(membership, { isFromAliasMember: true })}
  and ${getTimestampComparison(">")}
  `;
}

function getMyReadsAliasQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds, { isAliasFeed: true });

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = false 
  from feed_latest_event
  where ${getCommonItemConditions(membership, { isFromAliasMember: true, unreadStatus: true })}
  and ${getTimestampComparison("<=")}
  `;
}

function getOrganizerUnreadsAliasQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds, { isAliasFeed: true, teamDispatchMode: true });

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = true
  from feed_latest_event
  where ${getCommonItemConditions(membership, { isFromAliasMember: true })}
  and ${getTimestampComparison(">")}
  `;
}

function getOrganizerReadsAliasQuery(membership: WorkspaceMembership, feedIds?: string[]) {
  const cte = getFeedLatestEventCTE(membership, feedIds, { isAliasFeed: true, teamDispatchMode: true });

  return sql`
  with feed_latest_event as (${cte})
  update ${item}
  set "unread" = false
  from feed_latest_event
  where ${getCommonItemConditions(membership, { isFromAliasMember: true, unreadStatus: true })}
  and ${getTimestampComparison("<=")}
  `;
}

const UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY = "storyboard.unreadsAlsoSetAsReadOnNextBootstrap.v1";

/** Use before a full page reload (e.g. `hardReboot`); the next bootstrap should run `initializeUnreadsForAllFeeds` with `alsoSetAsRead: true`. In-memory debounced unread work is cleared by reload. */
export function setUnreadAlsoSetAsReadOnNextBootstrap(): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY, "1");
    }
  } catch {
    // quota / private mode
  }
}

export function consumeUnreadAlsoSetAsReadOnNextBootstrap(): boolean {
  try {
    if (typeof sessionStorage === "undefined") {
      return false;
    }
    if (sessionStorage.getItem(UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY) === "1") {
      sessionStorage.removeItem(UNREADS_ALSO_SET_AS_READ_ON_NEXT_BOOTSTRAP_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export async function initializeUnreadsForAllFeeds({
  membership,
  feedIds,
  alsoSetAsRead = false,
  tx,
}: {
  membership: WorkspaceMembership;
  feedIds?: string[];
  alsoSetAsRead: boolean;
  tx?: Tx;
}) {
  const startTime = new Date().getTime();
  const isOrganizer = membership.role === "member";
  const isDriver = membership.role === "limitedMember";

  if (feedIds !== undefined) {
    log.debug("[UNREADS][FEEDS] STARTING", {
      membership,
      feedIds,
      alsoSetAsRead,
      isOrganizer,
      isDriver,
    });
  } else {
    log.debug("[UNREADS][INITIAL] STARTING", {
      membership,
      alsoSetAsRead,
      isOrganizer,
      isDriver,
    });
  }

  const teamDispatch = fetchHideDriverMessageValue(isOrganizer);

  const dbWork = async (dx: Tx) => {
    if (isOrganizer && teamDispatch) {
      await executeQuery(getMyUnreadsNonAliasQuery(membership, feedIds), "myUnreadsNonAlias", dx);
      await executeQuery(getOrganizerUnreadsAliasQuery(membership, feedIds), "organizerUnreadsAlias", dx);

      if (alsoSetAsRead) {
        await executeQuery(getMyReadsNonAliasQuery(membership, feedIds), "myReadsNonAlias", dx);
        await executeQuery(getOrganizerReadsAliasQuery(membership, feedIds), "organizerReadsAlias", dx);
      }
    } else if (isDriver) {
      await executeQuery(getMyUnreadAllQuery(membership, feedIds), "myUnreadAll", dx);
      if (alsoSetAsRead) {
        await executeQuery(getMyReadAllQuery(membership, feedIds), "myReadAll", dx);
      }
    } else {
      await executeQuery(getMyUnreadsAliasQuery(membership, feedIds), "myUnreadsAlias", dx);
      await executeQuery(getMyUnreadsNonAliasQuery(membership, feedIds), "myUnreadsNonAlias", dx);
      if (alsoSetAsRead) {
        await executeQuery(getMyReadsAliasQuery(membership, feedIds), "myReadsAlias", dx);
        await executeQuery(getMyReadsNonAliasQuery(membership, feedIds), "myReadsNonAlias", dx);
      }
    }
  };

  try {
    if (tx) {
      await dbWork(tx);
    } else {
      await db.$client.transaction(async (dx) => {
        await dbWork(dx);
      });
    }
  } catch (e) {
    log.error("ERROR", e);
  }
  const endTime = new Date().getTime();
  if (feedIds !== undefined) {
    log.debug("[UNREADS][FEEDS]", endTime - startTime, {
      membership,
      feedIds,
      alsoSetAsRead,
      isOrganizer,
      isDriver,
      teamDispatch,
    });
  } else {
    log.debug("[UNREADS][INITIAL]", endTime - startTime, {
      membership,
      alsoSetAsRead,
      isOrganizer,
      isDriver,
      teamDispatch,
    });
  }
  emitTableUpdates(["item"]);
}

// Queue for debouncing unread calculations
type UnreadRequest = {
  membership: WorkspaceMembership;
  feedIds?: string[];
  alsoSetAsRead: boolean;
};

const unreadQueue: Record<string, UnreadRequest> = {};
let unreadTimer: NodeJS.Timeout | null = null;
const DEFAULT_UNREAD_DEBOUNCE_MS = 2000;

// Process the queued unread calculation requests
function processUnreadQueue() {
  const memberships = new Map<string, UnreadRequest>();

  // Group by membership and alsoSetAsRead, combining feedIds
  Object.values(unreadQueue).forEach((request) => {
    const key = `${request.membership.id}:${request.alsoSetAsRead}`;

    if (memberships.has(key)) {
      const existing = memberships.get(key)!;
      // Combine feedIds if both requests have them, otherwise use undefined
      if (existing.feedIds && request.feedIds) {
        existing.feedIds = [...new Set([...existing.feedIds, ...request.feedIds])];
      } else if (!existing.feedIds && request.feedIds) {
        existing.feedIds = [...request.feedIds];
      }
    } else {
      memberships.set(key, {
        membership: request.membership,
        feedIds: request.feedIds ? [...request.feedIds] : undefined,
        alsoSetAsRead: request.alsoSetAsRead,
      });
    }
  });

  // Clear the queue
  Object.keys(unreadQueue).forEach((key) => delete unreadQueue[key]);
  unreadTimer = null;

  // Process each combined request (only if there are any)
  if (memberships.size > 0) {
    memberships.forEach((request) => {
      initializeUnreadsForAllFeeds(request).catch((error) => {
        log.error("Failed to process unread calculation", error);
      });
    });
  }
}

export function enqueueCalculateUnreads({
  membership,
  feedIds,
  alsoSetAsRead = false,
}: {
  membership: WorkspaceMembership;
  feedIds?: string[];
  alsoSetAsRead: boolean;
}) {
  // Generate a unique key for this request
  const requestKey = `${membership.id}:${alsoSetAsRead}:${Date.now()}`;

  // Add to queue
  unreadQueue[requestKey] = {
    membership,
    feedIds,
    alsoSetAsRead,
  };

  // Only set a timer if one isn't already running
  if (!unreadTimer) {
    const debounceMs = typeof flags.unreadDebounceMs === "number" ? flags.unreadDebounceMs : DEFAULT_UNREAD_DEBOUNCE_MS;
    unreadTimer = setTimeout(processUnreadQueue, debounceMs);
  }
}

/*

As an organizer
Alias feed:
  Only the alias owner should trigger unread
  Only use shared account events if hideDriverMessagesWhenAnotherOrganizerReadsIt is true

Non-alias feed:
  Bots never trigger unread

*/
